import { useCallback, useEffect, useState } from 'react';
import { Box, Button, ButtonBase, Dialog, Stack, Typography, useMediaQuery } from '@mui/material';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import { Cifra, ALTURA_TACTIL } from 'src/components/ui/casa';
import { formatCurrencyWithCode } from 'src/utils/formatters';
import { fechaCortaUtc } from 'src/utils/sorbyIa/fechas';
import { monedaValida, montosDeMovimiento, otraMoneda } from 'src/utils/sorbyIa/obraResumen';

const CLAVE_MONEDA = 'sorbyIa.monedaObra';
const NOMBRE_MONEDA = { ARS: 'Pesos', USD: 'Dólares' };
const NOMBRE_MONEDA_CORTO = { ARS: '$', USD: 'US$' };

export const leerMoneda = () => {
  try {
    return monedaValida(window.localStorage.getItem(CLAVE_MONEDA));
  } catch {
    return 'ARS';
  }
};

const guardarMoneda = (moneda) => {
  try {
    window.localStorage.setItem(CLAVE_MONEDA, moneda);
  } catch {
    // Sin almacenamiento la preferencia dura lo que dura la pantalla.
  }
};

export const monto = (valor, moneda) => formatCurrencyWithCode(valor, moneda);

// Moneda principal de la pantalla; la elección queda en el navegador.
export function useMonedaPrincipal() {
  const [moneda, setMoneda] = useState('ARS');

  useEffect(() => {
    setMoneda(leerMoneda());
  }, []);

  const cambiar = useCallback((nueva) => {
    setMoneda(nueva);
    guardarMoneda(nueva);
  }, []);

  return [moneda, cambiar];
}

export function InterruptorMoneda({ moneda, onChange }) {
  const esCelular = useMediaQuery((theme) => theme.breakpoints.down('sm'), { noSsr: true });
  const nombres = esCelular ? NOMBRE_MONEDA_CORTO : NOMBRE_MONEDA;
  return (
    <Stack direction="row" role="group" aria-label="Moneda principal" sx={{ border: 1, borderColor: 'divider', borderRadius: 1.5, p: '3px' }}>
      {['ARS', 'USD'].map((m) => {
        const activa = moneda === m;
        return (
          <Button
            key={m}
            aria-pressed={activa}
            aria-label={NOMBRE_MONEDA[m]}
            onClick={() => onChange(m)}
            disableElevation
            variant={activa ? 'contained' : 'text'}
            color={activa ? 'primary' : 'inherit'}
            sx={{ minHeight: ALTURA_TACTIL, minWidth: esCelular ? 56 : 84, px: 1.5, fontWeight: 700, fontSize: 13, color: activa ? undefined : 'text.secondary' }}
          >
            {nombres[m]}
          </Button>
        );
      })}
    </Stack>
  );
}

export function Foto({ url, onAbrir }) {
  if (!url) {
    return (
      <Box aria-hidden sx={{ width: 52, height: 52, borderRadius: 1, bgcolor: 'action.hover', display: 'grid', placeItems: 'center', flexShrink: 0, color: 'text.disabled' }}>
        <ReceiptLongRoundedIcon fontSize="small" />
      </Box>
    );
  }
  return (
    <ButtonBase
      onClick={() => onAbrir(url)}
      aria-label="Ver comprobante"
      sx={{ width: 52, height: 52, borderRadius: 1, overflow: 'hidden', flexShrink: 0, border: 1, borderColor: 'divider' }}
    >
      <Box component="img" src={url} alt="" loading="lazy" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
    </ButtonBase>
  );
}

// `movimiento.obraNombre` (lo suma movimientosRecientesDeObras) dice de qué obra es: la lista mezcla obras.
export function TarjetaMovimiento({ movimiento, moneda, onAbrirFoto }) {
  const montos = montosDeMovimiento(movimiento);
  const otra = otraMoneda(moneda);
  const detalle = [movimiento.categoria, movimiento.proveedor].filter(Boolean).join(' · ');
  const fecha = [movimiento.obraNombre, fechaCortaUtc(movimiento.fecha)].filter(Boolean).join(' · ');
  const signo = movimiento.tipo === 'ingreso' ? '+' : '';
  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ py: 1.25, borderTop: 1, borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}>
      <Foto url={movimiento.urlImagen} onAbrir={onAbrirFoto} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>{detalle || 'Sin categoría'}</Typography>
        <Typography noWrap variant="caption" color="text.secondary" sx={{ display: 'block' }}>{fecha}</Typography>
      </Box>
      <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
        <Cifra tono={movimiento.tipo === 'ingreso' ? 'bueno' : 'neutro'}>{signo}{monto(montos[moneda], moneda)}</Cifra>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>
          {monto(montos[otra], otra)}
        </Typography>
      </Box>
    </Stack>
  );
}

// Fila de una obra: nombre a la izquierda, cifra principal y su equivalente a la derecha; lo demás va debajo.
export function FilaObra({ nombre, principal, secundaria, children }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2} sx={{ py: 1.25, borderTop: 1, borderColor: 'divider', '&:first-of-type': { borderTop: 0 } }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography noWrap sx={{ fontSize: 14, fontWeight: 600 }}>{nombre}</Typography>
        {children}
      </Box>
      {principal != null && (
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Cifra>{principal}</Cifra>
          {secundaria != null && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontVariantNumeric: 'tabular-nums' }}>
              {secundaria}
            </Typography>
          )}
        </Box>
      )}
    </Stack>
  );
}

export function VisorFoto({ url, onCerrar }) {
  return (
    <Dialog open={!!url} onClose={onCerrar} maxWidth="md" aria-label="Comprobante">
      {url && <Box component="img" src={url} alt="Comprobante del movimiento" sx={{ display: 'block', maxWidth: '100%', maxHeight: '85vh' }} />}
    </Dialog>
  );
}

export function BotonWhatsapp({ url, sx }) {
  return (
    <Button
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      variant="contained"
      startIcon={<WhatsAppIcon />}
      sx={{ minHeight: ALTURA_TACTIL, fontWeight: 700, ...sx }}
    >
      Seguir por WhatsApp
    </Button>
  );
}

export const AVISO_MOVIMIENTOS_TRUNCADOS = 'Hay más movimientos de los que se muestran: ver todos.';
